/**
 * user-configuration-response.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Group, GroupData } from "../../../auth/identity/group";
import { Self } from "../../../auth/identity/user";
import { ZoneData } from "../../../auth/identity/zone";
import { Rule } from "../../../auth/permissions/rules";
import { protocolDefault } from "../../../support/types/decorators";
import { validateAs, validateFor, validateOpaqueID, validateTSEnum }
from "../../validation/decorators";
import { IncomingMessage } from "../general/incoming-message";
import { MessageTag } from "../message-tag";

/**
 * The {@link UserConfigurationResponse} carries essential information from the
 * server about the currently- (usually, newly-) authenticated user. This
 * includes identifying and demographic information, as well as permissions,
 * roles, and group membership.
 */
@validateFor(MessageTag.RSP_CFG_USER)
export class UserConfigurationResponse extends IncomingMessage
{
    /** The unique tag for this message type */
    public tag : MessageTag = MessageTag.RSP_CFG_USER;

    /**
     * The display name of the user.l
     */
    @validateAs("string")
	public name : string;
	
	@validateAs("string")
	public login : string;

    /**
     * A human readable name for the organization the user belongs to. Note that
     * organizations are simply string labels and not necessarily unique. They
     * are also dependent on the user's active zone, rather than groups or group
     * networks.
     */
    @validateAs("string")
    public org : string;

    /**
     * Whether the user is a "support" user and should be able to access support
     * tools and support algorithms.
     */
    @validateAs("boolean")
    public support : boolean;
    // @protocolDefault(4, false)

    /**
     * Whether the user is a (system) developer and should have access to
     * special debugging and testing tools.
     */
    @validateAs("boolean", true, false)
    public dev : boolean;

    /**
     * Whether the user is a (system) developer and should have access to
     * special debugging and testing tools.
     */
    @validateAs("boolean", true, false)
    public guest : boolean;

    /**
     * The minimum number of secure prompts the user needs defined to satisfy
     * the requirements of their groups / zones.
     */
    @validateAs(["integer", {min: 0}])
    public minimumSecurePrompts : number;
    // @protocolDefault(4, 3)

    /**
     * A summary of the groups the user belongs to, limited to the groups this
     * zone admits (that is, the intersection between the two sets).
     */
    @validateAs({array: "object"}) // TODO: stronger validation
    public groups : GroupData[];

    /**
     * Whether this user has additional groups that aren't visible within this
     * zone.
     */
    @validateAs("boolean")
    public hasMoreGroups : boolean;

    /**
     * The "absolute" (not group-parameterized) permission rules the user has.
     */
    @validateTSEnum(Rule, true)
	public rules : Rule[];
	
	@validateAs({array: "object"}, true, []) // TODO: deoptionalize
	public zones : ZoneData[];

    // TODO: deprecate this somehow. it sux
    public static CreateUser (msg : UserConfigurationResponse) : Self
    {
        const self : Self =
            new Self(
				msg.name,
				msg.login,
                msg.org,
                msg.groups,
                msg.rules,
                msg.dev,
                msg.support,
				msg.minimumSecurePrompts,
        msg.zones,
        msg.guest
            );
        return self;
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/messages/metadata/user-configuration-response.ts